import { useGetStrings } from "@app/store/service/cmsService"
import { ORDER_LINE_TYPES, TICKET_TYPES } from "@app/utils/constants"
import { getPriceString, getTimeString } from "@app/utils/localeUtils"
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus"
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useViewport } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { useEffect, useState } from "react"
import "./OrderLine.css"

const BaseDescription = ({ children }) => {
	return (
		<div className="regular flex flex-items-center" style={{ width: "70%" }}>
			{children}
		</div>
	)
}

const DescriptionName = ({ children, orderLine }) => {
	return (
		<div id={`orderLine-${orderLine.details?.[0].id}`} className="regular">
			{children}
		</div>
	)
}

const Quantity = ({ orderLine, detail }) => {
	return (
		<div style={{ width: "27px" }} className="regular">
			{orderLine || detail ? <>{detail.quantity || orderLine.quantity}x</> : null}
		</div>
	)
}

const DefaultDescription = ({ orderLine, detail }) => {
	return (
		<BaseDescription>
			<Quantity orderLine={orderLine} detail={detail} />
			<DescriptionName orderLine={orderLine}>{detail.translation || detail.description}</DescriptionName>
		</BaseDescription>
	)
}
const LoungerDescription = ({ orderLine, detail }) => {
	const {
		data: { numberShort },
	} = useGetStrings(undefined, { defaultData: {} })
	return (
		<BaseDescription>
			<Quantity orderLine={orderLine} detail={detail} />
			<DescriptionName orderLine={orderLine}>{detail.translation || detail.description} </DescriptionName>
			<span className="ml-1">
				{numberShort}{" "}
				<span className="color-fg-secondary">
					{detail.zoneAbbreviation}-{detail.abbreviation}
					{detail.objectNumber || detail.number}
				</span>
			</span>
		</BaseDescription>
	)
}
const EntranceDescription = ({ orderLine, detail }) => {
	return (
		<BaseDescription>
			<Quantity orderLine={orderLine} detail={detail} />
			<DescriptionName orderLine={orderLine}>{detail.translation || detail.description}</DescriptionName>
			{/* <span className="color-fg-secondary">&nbsp;{detail.scanCode && <>{detail.scanCode}</>}</span> */}
		</BaseDescription>
	)
}
const VoucherDescription = ({ orderLine, detail }) => {
	return (
		<BaseDescription>
			<Quantity orderLine={orderLine} detail={detail} />
			<DescriptionName orderLine={orderLine}>{detail.translation || orderLine.description}</DescriptionName>
		</BaseDescription>
	)
}
const CollectionDescription = ({ orderLine, detail }) => {
	return (
		<BaseDescription>
			<Quantity orderLine={orderLine} detail={detail} /> <DescriptionName orderLine={orderLine}>{orderLine.description}</DescriptionName>
		</BaseDescription>
	)
}

const getOrderLineName = (orderLine, detail) => {
	let typeId = orderLine.typeId || detail.typeId
	if (typeId === ORDER_LINE_TYPES.Object || typeId === ORDER_LINE_TYPES.ObjectGroup) {
		return <LoungerDescription detail={detail} orderLine={orderLine} />
	}
	if (typeId === ORDER_LINE_TYPES.Ticket) {
		return <EntranceDescription detail={detail} orderLine={orderLine} />
	}
	if (typeId === ORDER_LINE_TYPES.Voucher) {
		return <VoucherDescription orderLine={orderLine} detail={detail} />
	}
	if (typeId === ORDER_LINE_TYPES.Collection) {
		return <CollectionDescription orderLine={orderLine} detail={detail} />
	}

	return <DefaultDescription detail={detail} orderLine={orderLine} />
}

const OrderlineAdditionalInfos = ({ orderLine, detail }) => {
	const [lines, setLines] = useState([])
	const visitorKinds = useSelector(state => state.order.visitorKinds)
	const visitorKindId = orderLine.details[0]?.visitorKindId

	const {
		data: { orderOverviewPriceEntranceFrom, orderOverviewBookingOnTransponder },
	} = useGetStrings(undefined, { defaultData: {} })

	useEffect(() => {
		if (detail.typeId === ORDER_LINE_TYPES.Ticket) {
			let lines = []
			if (detail.timeBlockTypeId !== TICKET_TYPES.WholeDay && detail.timeFrom) {
				let ts = detail.timeFrom.split(":")
				let date = new Date()
				date.setHours(Number(ts[0]))
				date.setMinutes(Number(ts[1]))

				lines.push(
					<div key="entranceFrom">
						{orderOverviewPriceEntranceFrom} {getTimeString(date)}
					</div>,
				)
			}

			if (visitorKinds && visitorKindId) {
				const rateTitle = visitorKinds[visitorKindId]?.visitor?.rateTitle
				if (rateTitle) {
					lines.push(
						<div key="entranceFrom" className="orderLine rateTitle">
							{rateTitle}
						</div>,
					)
				}
			}

			if (orderLine.transponderNumber) {
				lines.push(
					<div key="entranceFrom">
						{orderOverviewBookingOnTransponder}: {orderLine.transponderNumber}
					</div>,
				)
			}
			setLines(lines)
		} else if (detail.typeId === ORDER_LINE_TYPES.Activity) {
			let lines = []
			if (detail.timeBlockTypeId !== TICKET_TYPES.WholeDay && detail.timeFrom) {
				let ts = detail.timeFrom.split(":")
				let date = new Date()
				date.setHours(Number(ts[0]))
				date.setMinutes(Number(ts[1]))
				lines.push(<div key="entranceFrom">{getTimeString(date)}</div>)
			}

			setLines(lines)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detail])
	return lines.map((line, i) => {
		return (
			<div className="flex" key={i}>
				<Quantity /> {line}
			</div>
		)
	})
}

const OrderLineName = ({ orderLine, detail, selectionComponent }) => {
	const { sm, md } = useViewport()
	let sum = orderLine.details.reduce((v, o) => {
		return v + o.amounts[0].grossAmount + o.amounts.filter((e, i) => i !== 0 && e.hide === true).reduce((a, b) => a + b.grossAmount, 0)
	}, 0)
	return (
		<>
			<div className="flex flex-justify-between">
				{/* Description */}
				{getOrderLineName(orderLine, detail)}

				{selectionComponent}

				{/* PriceValue */}
				<div style={{ width: sm || md ? "80px" : "100px" }} className="regular text-right flex flex-items-center flex-justify-end">
					{getPriceString(sum, undefined, false)}
				</div>
			</div>
		</>
	)
}

const OrderSubLineDescription = ({ orderLine, detail }) => {
	return (
		<div className="">
			{/* Description */}
			{getOrderLineName(orderLine, detail)}
		</div>
	)
}

const OrderLineAmounts = ({ details }) => {
	let lines = []

	details.forEach(detail => {
		detail.amounts.forEach((amount, index) => {
			if (amount.hide == true || index === 0) return

			lines.push(amount)
			// if (index !== 0 && amount.hide !== true) {
			//     if (lines[amount.description]) {
			//         lines[amount.description] += amount.grossAmount;
			//     } else {
			//         lines[amount.description] = amount.grossAmount;
			//     }
			// }
		})
	})

	return (
		<>
			{lines.map((amt, amtIndex) => {
				return (
					<div key={amtIndex} className="flex flex-justify-between">
						<div className="flex">
							<Quantity /> {amt.description || "discount"}
							{amt.priceActionCode ? " - " + amt.priceActionCode : ""}
						</div>
						<div style={{ width: "80px" }} className="text-right">
							{getPriceString(amt.grossAmount, undefined, false)}
						</div>
					</div>
				)
			})}
		</>
	)
}

const OrderLineDetails = ({ orderLine }) => {
	//we doesn't display details if the orderLine is vocher
	if (
		orderLine.typeId === ORDER_LINE_TYPES.Collection ||
		orderLine.typeId === ORDER_LINE_TYPES.Voucher ||
		orderLine.details[0].typeId === ORDER_LINE_TYPES.Voucher
	) {
		return null
	}

	return (
		<>
			{orderLine.details.map((line, i) => {
				if (i === 0) return null //we ignore the first line because that is used for the main Line

				return (
					<div className="orderLine f5 ml-5">
						<OrderSubLineDescription key={i} orderLine={orderLine} detail={line} />
					</div>
				)
			})}
		</>
	)
}

const OrderLineTransporter = ({ orderLine, detail }) => {
	const { transporter } = orderLine
	const {
		data: { delivery },
	} = useGetStrings(undefined, { defaultData: {} })

	let typeId = detail.originalTypeId || detail.typeId

	if (transporter && typeId !== ORDER_LINE_TYPES.Ticket && typeId !== ORDER_LINE_TYPES.Object && typeId !== ORDER_LINE_TYPES.ObjectGroup) {
		return (
			<div className="flex">
				<Quantity />
				<span>
					{delivery}:{" "}
					<span className="color-fg-secondary" style={{ fontStyle: "italic" }}>
						{transporter}
					</span>
				</span>
			</div>
		)
	}

	return null
}

const OrderLineDescription = ({ orderLine, selectionComponent }) => {
	const firstDetail = orderLine.details[0]

	return (
		<div>
			<OrderLineName orderLine={orderLine} detail={firstDetail} selectionComponent={selectionComponent} />

			<OrderlineAdditionalInfos orderLine={orderLine} detail={firstDetail} />
			<OrderLineTransporter orderLine={orderLine} detail={firstDetail} />
			<OrderLineAmounts details={orderLine.details} />
		</div>
	)
}

const useGetSelectionComponent = (isEditable, showMinus, onMinus, onPlus, quantity, showPlus = true, orderLineId) => {
	const {
		data: { buttonLabelDecreaseQuantity, buttonLabelIncreaseQuantity },
	} = useGetStrings(undefined, { defaultData: {} })

	if (!isEditable) {
		return <></>
	} else {
		return (
			<div role="group" aria-labelledby={orderLineId} className="flex flex-items-center ml-2">
				{showMinus && (
					<button
						className="counter-btn color-fg-primary flex flex-center circle border color-border-primary f5 p-1 clickable"
						onClick={onMinus}
						style={{
							height: "24px",
							width: "24px",
						}}
						aria-label={buttonLabelDecreaseQuantity}
					>
						<FontAwesomeIcon icon={faMinus} />
					</button>
				)}

				<div className="f4 mx-2 text-center" style={{ width: "15px" }}>
					{quantity || 0}
				</div>
				{!(showPlus === false) && (
					<button
						className="counter-btn color-fg-primary flex flex-center circle border color-border-primary f5 p-1 clickable"
						onClick={onPlus}
						style={{
							height: "24px",
							width: "24px",
						}}
						aria-label={buttonLabelIncreaseQuantity}
					>
						<FontAwesomeIcon icon={faPlus} />
					</button>
				)}
			</div>
		)
	}
}

export default function OrderLine({ orderLine, ignoreIds, showMinus, isEditable, onMinus, onPlus, showPlus }) {
	const priceActions = useSelector(state => state.order.priceActions)
	//ignore the orderLine if all details are ignored
	const render = orderLine.details.filter(e => (!ignoreIds ? false : ignoreIds.indexOf(e.typeId) !== -1)).length === 0

	const orderLineId = `orderLine-${orderLine.details?.[0].id}`

	const firstDetail = orderLine.details[0]
	const selectionComponent = useGetSelectionComponent(isEditable, showMinus, onMinus, onPlus, orderLine.quantity, showPlus, orderLineId)

	if (!render) {
		return null
	}
	if (firstDetail.feeId !== undefined && firstDetail.feeId !== null) {
		//we ignore the transporter fee articleLine
		return null
	}

	//is used as a priceaction
	let isUsedAsPriceAction =
		(firstDetail.typeId === ORDER_LINE_TYPES.Ticket || firstDetail.typeId === ORDER_LINE_TYPES.Object || firstDetail.typeId === ORDER_LINE_TYPES.ObjectGroup) &&
		(priceActions || []).find(e => e.scanCode === firstDetail.scanCode)
	if (isUsedAsPriceAction) {
		return null
	}

	return (
		<li className="orderLineItem">
			<div className="orderLine f5">
				<OrderLineDescription orderLine={orderLine} selectionComponent={selectionComponent} />
			</div>
			<OrderLineDetails orderLine={orderLine} />
		</li>
	)
}
